import {Component, effect, ElementRef, inject, input, OnInit, output, ViewChild} from '@angular/core';
import {ImagesService} from '../../../../services/general/images.service';
import {CustomInputComponent} from "../../../input/custom-input/custom-input.component";
import {CustomSelectInputComponent, SelectOption} from "../../../input/custom-select-input/custom-select-input.component";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {InstitutionService} from '../../../../services/institution/institution.service';
import {AdminService} from '../../../../services/admin/admin.service';
import {PrimaryButtonComponent} from "../../../buttons/primary-button/primary-button.component";
import {ToastService} from '../../../../services/general/toast.service';
import {Institution} from '../../../../interfaces/responses/institution.interface';

const UNASSIGNED_OPTION: SelectOption = { label: 'None / Unassigned', value: '' };

@Component({
  selector: 'app-add-institution',
  imports: [
    CustomInputComponent,
    CustomSelectInputComponent,
    CommonModule,
    ReactiveFormsModule,
    PrimaryButtonComponent,
],
  templateUrl: './add-institution.component.html',
  styleUrl: './add-institution.component.scss'
})
export class AddInstitutionComponent implements OnInit {
  _images = inject(ImagesService);
  categories = [];
  csvFile: File | null = null;
  documents: File[] = [];
  @ViewChild('fileInput') fileInputRef!: ElementRef;
  @ViewChild('csvFileInput') csvFileInputRef!: ElementRef;
  _fb = inject(FormBuilder);
  institutionService = inject(InstitutionService);
  adminService = inject(AdminService);
  isLoading: boolean = false;
  isDisabled: boolean = false;
  _toast = inject(ToastService);
  showAddCSV = input<boolean>(false);
  institution = input<Institution | null>(null);
  autoRepaymentValue: boolean = false;
  isNiaVerificationRequiredValue: boolean = false;
  validateLoanParameterValue: boolean = false;

  assignedToOptions: SelectOption[] = [UNASSIGNED_OPTION];
  isLoadingBdUsers: boolean = false;
  bdUsersUnavailable: boolean = false;

  addInstitutionForm: FormGroup = this._fb.group({
    name: ['', [Validators.required]],
    address: ['', [Validators.required]],
    contact: ['', [Validators.required, Validators.pattern(/^\+?[0-9\s]+$/)]],
    description: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    autoRepayment: [false],
    isNiaVerificationRequired: [false],
    validateLoanParameter: [false],
    assignedTo: [''],
  });

  ngOnInit(): void {
    this.loadBusinessDevelopmentUsers();
    this.addInstitutionForm.get('assignedTo')?.valueChanges.subscribe(() => {
      const control = this.addInstitutionForm.get('assignedTo');
      if (control?.hasError('apiError')) {
        control.setErrors(null);
      }
    });
  }

  private loadBusinessDevelopmentUsers(): void {
    this.isLoadingBdUsers = true;
    this.adminService.getBusinessDevelopmentUsers().subscribe({
      next: (users) => {
        this.assignedToOptions = [
          UNASSIGNED_OPTION,
          ...users.map(u => ({ label: `${u.firstName} ${u.lastName} — ${u.email}`, value: u.id })),
        ];
        this.bdUsersUnavailable = users.length === 0;
        this.isLoadingBdUsers = false;
        if (this.bdUsersUnavailable) {
          this.addInstitutionForm.get('assignedTo')?.disable();
        } else {
          this.addInstitutionForm.get('assignedTo')?.enable();
        }
      },
      error: (error) => {
        console.error('Error loading Business Development users:', error);
        this.isLoadingBdUsers = false;
        this.bdUsersUnavailable = true;
        this.addInstitutionForm.get('assignedTo')?.disable();
        this._toast.error('Failed to load Business Development staff list');
      },
    });
  }

  private handleSaveError(error: HttpErrorResponse): void {
    const rawMessage = error?.error?.message;
    const message = Array.isArray(rawMessage) ? rawMessage.join(', ') : rawMessage;
    if (typeof message === 'string' && message.toLowerCase().includes('business development')) {
      this.addInstitutionForm.get('assignedTo')?.setErrors({ apiError: message });
      this.addInstitutionForm.get('assignedTo')?.markAsTouched();
      return;
    }
    this._toast.error(message || (this.institution() ? 'Error updating institution' : 'Error creating institution'));
  }

  // React to changes in the 'institution' input and prefill/reset the form accordingly.
  private prefillFormFromInstitution = effect(() => {
    const inst = this.institution(); // depend on input signal
    if (inst) {
      // Handle both string 'Yes'/'No' and boolean true/false from API
      const autoRepayment = inst.autoRepayment;
      this.autoRepaymentValue = autoRepayment === true || String(autoRepayment).toLowerCase() === 'yes' || String(autoRepayment) === 'true';
      this.isNiaVerificationRequiredValue = inst.isNiaVerificationRequired === true;
      this.validateLoanParameterValue = inst.validateLoanParameter === true;
      this.addInstitutionForm.patchValue({
        name: inst.name ?? '',
        address: inst.address ?? '',
        contact: inst.contact ?? '',
        description: inst.description ?? '',
        email: inst.email ?? '',
        autoRepayment: this.autoRepaymentValue,
        isNiaVerificationRequired: this.isNiaVerificationRequiredValue,
        validateLoanParameter: this.validateLoanParameterValue,
        assignedTo: inst.assignedTo?.id ?? '',
      });
      this.addInstitutionForm.markAsPristine();
      this.addInstitutionForm.markAsUntouched();
    } else {
      // reset to empty values explicitly
      this.autoRepaymentValue = false;
      this.isNiaVerificationRequiredValue = false;
      this.validateLoanParameterValue = false;
      this.addInstitutionForm.reset({
        name: '',
        address: '',
        contact: '',
        description: '',
        email: '',
        autoRepayment: false,
        isNiaVerificationRequired: false,
        validateLoanParameter: false,
        assignedTo: '',
      });
      this.addInstitutionForm.markAsPristine();
      this.addInstitutionForm.markAsUntouched();
    }
  });

  toggleAutoRepayment() {
    this.autoRepaymentValue = !this.autoRepaymentValue;
    this.addInstitutionForm.patchValue({ autoRepayment: this.autoRepaymentValue });
  }

  toggleIsNiaVerificationRequired() {
    this.isNiaVerificationRequiredValue = !this.isNiaVerificationRequiredValue;
    this.addInstitutionForm.patchValue({ isNiaVerificationRequired: this.isNiaVerificationRequiredValue });
  }

  toggleValidateLoanParameter() {
    this.validateLoanParameterValue = !this.validateLoanParameterValue;
    this.addInstitutionForm.patchValue({ validateLoanParameter: this.validateLoanParameterValue });
  }

  onSubmit() {
    if (this.addInstitutionForm.invalid) {
      this.addInstitutionForm.markAllAsTouched();
      return;
    }
    if (this.institution()) {
      this.updateInstitution();
    } else {
      this.createInstitution();
    }
  }

  createInstitution() {
    const { name, address, contact, description, email, autoRepayment, isNiaVerificationRequired, validateLoanParameter } = this.addInstitutionForm.value as {
      name?: string; address?: string; contact?: string; description?: string; email?: string; autoRepayment?: boolean; isNiaVerificationRequired?: boolean; validateLoanParameter?: boolean;
    };

    const formData = new FormData();
    formData.append('name', name ?? '');
    formData.append('address', address ?? '');
    formData.append('contact', contact ?? '');
    formData.append('description', description ?? '');
    formData.append('email', email ?? '');
    formData.append('autoRepayment', String(autoRepayment ?? false));
    formData.append('isNiaVerificationRequired', String(isNiaVerificationRequired ?? false));
    formData.append('validateLoanParameter', String(validateLoanParameter ?? false));
    const assignedTo = this.addInstitutionForm.get('assignedTo')?.value;
    if (assignedTo) {
      formData.append('assignedToId', assignedTo);
    }
    if (this.csvFile) {
      formData.append('member_list', this.csvFile, this.csvFile.name);
    }
    if (this.documents.length > 0) {
      this.documents.forEach((file) => {
        formData.append('documents', file, file.name);
      });
    }

    this.isLoading = true;
    this.institutionService.createInstitution(formData).subscribe({
      next: (response) => {
        this.loadInstitutionData();
        this.addInstitutionForm.reset();
        this.autoRepaymentValue = false;
        this.isNiaVerificationRequiredValue = false;
        this.validateLoanParameterValue = false;
        this._toast.success('Institution created successfully');
        this.onClose();
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.handleSaveError(error);
      }
    });
  }

  private updateInstitution() {
    const inst = this.institution();
    if (!inst?.id) return;

    const { name, address, contact, description, email, autoRepayment, isNiaVerificationRequired, validateLoanParameter } = this.addInstitutionForm.value as {
      name?: string; address?: string; contact?: string; description?: string; email?: string; autoRepayment?: boolean; isNiaVerificationRequired?: boolean; validateLoanParameter?: boolean;
    };

    const formData = new FormData();
    formData.append('name', name ?? '');
    formData.append('address', address ?? '');
    formData.append('contact', contact ?? '');
    formData.append('description', description ?? '');
    formData.append('email', email ?? '');
    formData.append('autoRepayment', String(autoRepayment ?? false));
    formData.append('isNiaVerificationRequired', String(isNiaVerificationRequired ?? false));
    formData.append('validateLoanParameter', String(validateLoanParameter ?? false));
    // Always send assignedToId (even empty) so clearing an existing assignment on update works.
    formData.append('assignedToId', this.addInstitutionForm.get('assignedTo')?.value ?? '');
    if (this.csvFile) {
      formData.append('member_list', this.csvFile, this.csvFile.name);
    }
    if (this.documents.length > 0) {
      this.documents.forEach((file) => {
        formData.append('documents', file, file.name);
      });
    }

    this.isLoading = true;
    this.institutionService.updateInstitution(inst.id, formData).subscribe({
      next: (response) => {
        this._toast.success('Institution updated successfully');
        this.loadInstitutionData();
        this.onClose();
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error updating institution:', error);
        this.isLoading = false;
        this.handleSaveError(error);
      }
    });
  }


  loadInstitutionEvent = output<void>()
  loadInstitutionData() {
    this.loadInstitutionEvent.emit();
  }


  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      const maxSize = 10 * 1024 * 1024;
      const newFiles = Array.from(target.files).filter(file => {
        if (!allowedTypes.includes(file.type)) {
          this._toast.error(`"${file.name}" is not a supported file type.`);
          return false;
        }
        if (file.size > maxSize) {
          this._toast.error(`"${file.name}" exceeds the 10MB size limit.`);
          return false;
        }
        return true;
      });
      if (newFiles.length) {
        this.documents = [...this.documents, ...newFiles];
      }
      if (this.fileInputRef?.nativeElement) {
        this.fileInputRef.nativeElement.value = '';
      }
    }
  }

  onCSVFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        this._toast.error('Only CSV files are allowed.');
        target.value = '';
        return;
      }
      this.csvFile = file;
    }
  }

  removeCSVFile(): void {
    this.csvFile = null;
    if (this.csvFileInputRef?.nativeElement) {
      this.csvFileInputRef.nativeElement.value = '';
    }
  }

  removeDocument(index: number): void {
    this.documents.splice(index, 1);
  }

  onCloseEvent = output<void>()
  onClose() {
    // Reset form fields to empty strings
    this.autoRepaymentValue = false;
    this.isNiaVerificationRequiredValue = false;
    this.validateLoanParameterValue = false;
    this.addInstitutionForm.reset({
      name: '',
      address: '',
      contact: '',
      description: '',
      email: '',
      autoRepayment: false,
      isNiaVerificationRequired: false,
      validateLoanParameter: false,
      assignedTo: '',
    });
    this.addInstitutionForm.markAsPristine();
    this.addInstitutionForm.markAsUntouched();
    this.addInstitutionForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    // Clear selected CSV file and documents
    this.csvFile = null;
    this.documents = [];
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
    if (this.csvFileInputRef?.nativeElement) {
      this.csvFileInputRef.nativeElement.value = '';
    }

    this.onCloseEvent.emit();
  }

  downloadSampleCSV() {
    // Fetch the sample.xlsx file from the public folder
    fetch('/sample.xlsx')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch sample file');
        }
        return response.blob();
      })
      .then(blob => {
        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'sample.xlsx';
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL object
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Error downloading sample file:', error);
        this._toast.error('Failed to download sample file');
      });
  }
}
